/**
 * Hook for managing resizable panel behavior
 *
 * Provides drag-to-resize functionality with min/max constraints
 * and optional localStorage persistence.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface UseResizablePanelOptions {
  /** Default width/height in pixels */
  defaultSize: number
  /** Minimum size in pixels */
  minSize: number
  /** Maximum size in pixels */
  maxSize: number
  /** localStorage key for persistence (optional) */
  storageKey?: string
  /** Which side the panel is on (affects drag direction) */
  side: 'left' | 'right' | 'top' | 'bottom'
}

export interface UseResizablePanelReturn {
  /** Current size in pixels */
  size: number
  /** Whether the panel is currently being dragged */
  isDragging: boolean
  /** Handler for the mouse down event on the resize handle */
  handleMouseDown: (e: React.MouseEvent) => void
  /** Reset to default size */
  resetSize: () => void
  /** Manually set the size */
  setSize: (size: number) => void
}

/**
 * Hook for creating a resizable panel
 */
export function useResizablePanel(
  options: UseResizablePanelOptions
): UseResizablePanelReturn {
  const { defaultSize, minSize, maxSize, storageKey, side } = options

  // Initialize size from localStorage or default
  const [size, setSizeState] = useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = parseInt(stored, 10)
        if (!isNaN(parsed)) {
          return Math.min(maxSize, Math.max(minSize, parsed))
        }
      }
    }
    return defaultSize
  })

  const [isDragging, setIsDragging] = useState(false)
  const startPosRef = useRef(0)
  const startSizeRef = useRef(0)

  /**
   * Handle mouse down on resize handle
   */
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)

      // Store starting position based on panel orientation
      if (side === 'left' || side === 'right') {
        startPosRef.current = e.clientX
      } else {
        startPosRef.current = e.clientY
      }
      startSizeRef.current = size
    },
    [size, side]
  )

  /**
   * Handle mouse movement during drag
   */
  useEffect(() => {
    if (!isDragging) return

    const handleMouseMove = (e: MouseEvent) => {
      let delta: number

      if (side === 'left' || side === 'right') {
        delta = e.clientX - startPosRef.current
      } else {
        delta = e.clientY - startPosRef.current
      }

      // Invert delta for right/bottom panels (dragging left/up increases size)
      if (side === 'right' || side === 'bottom') {
        delta = -delta
      }

      const newSize = Math.min(
        maxSize,
        Math.max(minSize, startSizeRef.current + delta)
      )
      setSizeState(newSize)
    }

    const handleMouseUp = () => {
      setIsDragging(false)

      // Persist to localStorage
      if (storageKey) {
        localStorage.setItem(storageKey, size.toString())
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    // Add cursor style to body during drag
    document.body.style.cursor =
      side === 'left' || side === 'right' ? 'col-resize' : 'row-resize'
    document.body.style.userSelect = 'none'

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, minSize, maxSize, side, storageKey, size])

  /**
   * Reset to default size
   */
  const resetSize = useCallback(() => {
    setSizeState(defaultSize)
    if (storageKey) {
      localStorage.removeItem(storageKey)
    }
  }, [defaultSize, storageKey])

  /**
   * Manually set size
   */
  const setSize = useCallback(
    (newSize: number) => {
      const constrainedSize = Math.min(maxSize, Math.max(minSize, newSize))
      setSizeState(constrainedSize)
      if (storageKey) {
        localStorage.setItem(storageKey, constrainedSize.toString())
      }
    },
    [minSize, maxSize, storageKey]
  )

  return {
    size,
    isDragging,
    handleMouseDown,
    resetSize,
    setSize,
  }
}
